import React, { useState, useEffect } from 'react';
import { AddServicesIcon } from '../../../../../components/common/SvgIcon';
import AddServiceModal from '../../AddServiceModal';
import { Toast } from '../../../../../components/common/Toast';
import ConfirmationModal from '../../../../../components/common/ConfirmationModal';

const DEFAULT_SERVICES = [
  { id: 1,  name: 'AWIN',                                          regex: 'www\\.dwin1\\.com\\/' },
  { id: 2,  name: 'Facebook Pixel',                                regex: 'connect\\.facebook\\.net|www\\.facebook\\.com\\/tr' },
  { id: 3,  name: 'Facebook Social Plugins',                       regex: 'www\\.facebook\\.com\\/plugins\\/like\\.php|www\\.facebook\\.com\\/plugins\\/ajax\\.php\\?action\\=likeboxfrontend' },
  { id: 4,  name: 'Google Adsense',                                regex: 'pagead2\\.googlesyndication\\.com\\/pagead\\/js\\/adsbygoogle\\.js' },
  { id: 5,  name: 'Google AdWords Remarketing/Conversion Tag',     regex: 'googleadservices\\.com\\/pagead\\/conversion\\.js|googleads\\.g\\.doubleclick' },
  { id: 6,  name: 'Google Analytics',                              regex: 'google-analytics\\.com\\/analytics\\.js' },
  { id: 7,  name: 'Google Gtag',                                   regex: 'googletagmanager\\.com\\/gtag\\/js' },
  { id: 8,  name: 'Google Maps',                                   regex: 'maps\\.google\\.com|google\\.\\[\\w\\.\\]+\\/maps\\/|maps\\.googleapis\\.com' },
  { id: 9,  name: 'Google Tag Manager',                            regex: 'www\\.googletagmanager\\.com\\/gtm\\.js' },
  { id: 10, name: 'Instagram',                                     regex: 'instagram\\.com\\/embed\\.js|platform\\.instagram\\.com\\/.*\\/embeds\\.js' },
  { id: 11, name: 'Jetpack Stats',                                 regex: '\\/\\/stats\\.wp\\.com' },
  { id: 12, name: 'LinkedIn Pixel',                                regex: 'snap\\.licdn\\.com' },
  { id: 13, name: 'Matomo',                                        regex: '\\/piwik\\.js|\\/matomo\\.js' },
  { id: 14, name: 'Twitter',                                       regex: 'platform\\.twitter\\.com' },
  { id: 15, name: 'Vimeo',                                         regex: 'player\\.vimeo\\.com\\/video' },
  { id: 16, name: 'YouTube',                                       regex: 'youtube\\.com\\/embed' },
];

const AvailableServicesTab = () => {
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    serviceId: null,
    isLoading: false,
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-available-services`, {
        headers: { 'X-WP-Nonce': LEGAL_PAGES.nonce },
      });
      const result = await res.json();
      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        setServices(result.data);
      }
    } catch (e) {
      // use defaults
    }
  };

  const handleAddService = (newService) => {
    const added = {
      id: Date.now(),
      name: newService.name.trim(),
      regex: newService.regex.trim(),
    };
    setServices((prev) => [...prev, added]);
    setShowModal(false);
    Toast.success(`"${added.name}" added.`);
  };

  const handleDelete = (id) => {
    setDeleteModal({ isOpen: true, serviceId: id, isLoading: false });
  };

  const confirmDeleteService = () => {
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    setServices((prev) => prev.filter((s) => s.id !== deleteModal.serviceId));
    setDeleteModal({ isOpen: false, serviceId: null, isLoading: false });
    Toast.success('Service deleted.');
  };

  const cancelDeleteService = () => {
    setDeleteModal({ isOpen: false, serviceId: null, isLoading: false });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-available-services`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify({ services }),
      });
      const result = await res.json();
      if (result.success) {
        Toast.success('Services saved!');
      } else {
        Toast.error('Failed to save services.');
      }
    } catch (e) {
      Toast.error('An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AddServiceModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleAddService}
      />

      {/* Add Service button */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 bg-[#17171C] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#3f3f46] transition"
        >
          {AddServicesIcon}
          ADD SERVICE
        </button>
      </div>

      {/* Services table */}
      <div className="border border-[#E4E4E7] rounded-xl overflow-hidden">
        <div className="max-h-[350px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
                <th className="text-left px-4 py-3 font-semibold text-[#17171C] w-44">Service Name</th>
                <th className="text-left px-4 py-3 font-semibold text-[#17171C]">Identifier Regex</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E4E7]">
              {services.map((svc) => (
                <tr key={svc.id} className="hover:bg-[#FAFAFA] transition">
                  <td className="px-4 py-3 text-[#17171C] font-medium align-top">{svc.name}</td>
                  <td className="px-4 py-3 text-[#6F6F7B] font-mono text-xs break-all align-top">{svc.regex}</td>
                  <td className="px-4 py-3 align-top">
                    <button
                      type="button"
                      onClick={() => handleDelete(svc.id)}
                      className="text-[#6F6F7B] hover:text-red-500 transition"
                      title="Delete service"
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#7C3BED] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Services'}
        </button>
      </div>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={cancelDeleteService}
        onConfirm={confirmDeleteService}
        title="Delete Service"
        message="Are you sure you want to delete this service? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteModal.isLoading}
      />
    </div>
  );
};

export default AvailableServicesTab;